import { useState, useCallback, useMemo } from 'react';

/**
 * useTableSelection
 *
 * Custom hook for managing table row selection state.
 * Supports single selection, multi-selection, and select-all functionality.
 */
const useTableSelection = (initialSelectedIds = []) => {
  const [selectedIds, setSelectedIds] = useState(new Set(initialSelectedIds));

  // Toggle selection for a single item
  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Select specific items
  const selectItems = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  // Add items to selection
  const addToSelection = useCallback((ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  // Remove items from selection
  const removeFromSelection = useCallback((ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Select all items from a given list
  const selectAll = useCallback((allIds) => {
    setSelectedIds(new Set(allIds));
  }, []);

  // Toggle all - if all selected, clear; otherwise select all
  const toggleAll = useCallback((allIds) => {
    setSelectedIds(prev => {
      const allSelected = allIds.every(id => prev.has(id));
      if (allSelected) {
        return new Set();
      }
      return new Set(allIds);
    });
  }, []);

  // Check if an item is selected
  const isSelected = useCallback((id) => {
    return selectedIds.has(id);
  }, [selectedIds]);

  // Computed values
  const selectedCount = selectedIds.size;

  const getSelectionState = useCallback((allIds) => {
    if (allIds.length === 0) {
      return { isAllSelected: false, isPartiallySelected: false };
    }
    const selectedCount = allIds.filter(id => selectedIds.has(id)).length;
    return {
      isAllSelected: selectedCount === allIds.length,
      isPartiallySelected: selectedCount > 0 && selectedCount < allIds.length
    };
  }, [selectedIds]);

  // Get array of selected IDs
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return {
    selectedIds,
    selectedArray,
    selectedCount,
    toggleSelection,
    selectItems,
    addToSelection,
    removeFromSelection,
    clearSelection,
    selectAll,
    toggleAll,
    isSelected,
    getSelectionState
  };
};

export default useTableSelection;
